import React, { useState, useEffect } from 'react';
import { Save, FileText } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminContent = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_cta_text: '',
    hero_cta_link: '',
    featured_section_title: '',
    featured_section_subtitle: '',
    categories_section_title: '',
    newsletter_title: '',
    newsletter_subtitle: ''
  });

  useEffect(() => { loadContent(); }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth('/api/admin/content/home');
      if (res.ok) {
        const data = await res.json();
        setContent(prev => ({ ...prev, ...data }));
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContent(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await fetchWithAuth('/api/admin/content/home', { method: 'PUT', body: content });
      if (res.ok) {
        toast.success('Contenido guardado');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-content">
      <div>
        <h2 className="text-2xl font-semibold text-white">Contenido del Home</h2>
        <p className="text-gray-400 text-sm">Edita los textos de la página principal</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Hero Section */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <FileText size={20} className="text-amber-500" />
            Sección Hero
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Título Principal</label>
              <input
                type="text"
                name="hero_title"
                value={content.hero_title}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                placeholder="Fragancias de Lujo Accesibles"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Subtítulo</label>
              <textarea
                name="hero_subtitle"
                value={content.hero_subtitle}
                onChange={handleChange}
                rows={2}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500 resize-none"
                placeholder="Descubre nuestra colección..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Texto del Botón</label>
                <input
                  type="text"
                  name="hero_cta_text"
                  value={content.hero_cta_text}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                  placeholder="Ver Colección"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Enlace del Botón</label>
                <input
                  type="text"
                  name="hero_cta_link"
                  value={content.hero_cta_link}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                  placeholder="/products"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Featured Section */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Sección Destacados</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Título</label>
              <input
                type="text"
                name="featured_section_title"
                value={content.featured_section_title}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                placeholder="Destacados"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Subtítulo</label>
              <input
                type="text"
                name="featured_section_subtitle"
                value={content.featured_section_subtitle}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                placeholder="Los favoritos de nuestros clientes"
              />
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Sección Categorías</h3>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Título</label>
            <input
              type="text"
              name="categories_section_title"
              value={content.categories_section_title}
              onChange={handleChange}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
              placeholder="Explora por Categoría"
            />
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Sección Newsletter</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Título</label>
              <input
                type="text"
                name="newsletter_title"
                value={content.newsletter_title}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                placeholder="Únete a Alphatica"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Subtítulo</label>
              <input
                type="text"
                name="newsletter_subtitle"
                value={content.newsletter_subtitle}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                placeholder="Recibe ofertas exclusivas y novedades"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <Save size={18} />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminContent;
